// symptomEvidencePdf.js — "Symptom Evidence Summary" + "C&P Exam Prep Sheet"
// exports. 100% deterministic — built straight from the veteran's own log via
// lib/symptomDiary.buildEvidenceSummary(). Reuses the Canvas/header/footer
// primitives from symptomLogPdf so all symptom exports look like one family.

import { PDFDocument, StandardFonts } from "pdf-lib";
import {
  Canvas, wrap, formatDate, severityColor,
  drawHeader, drawWatermark, drawFooter,
  PAGE_W, PAGE_H, M,
  C_BRAND, C_BRAND_DARK, C_MUTED, C_BORDER, C_BG, C_RED, C_GREEN,
} from "./symptomLogPdf";
import { buildEvidenceSummary, groupEntriesByCondition, fmtHours } from "./symptomDiary";
import { buildEpisodeSummary, buildEpisodeNarrative } from "./episodes";

const CREDIBILITY_NOTE =
  "Track symptoms accurately. Do not exaggerate. Consistent, specific logs are more " +
  "useful than dramatic wording. This document is a private symptom diary export and a " +
  "VA claim organization aid - it is not medical advice and not a guaranteed rating outcome.";

// ---- shared drawing helpers -----------------------------------------------------

function scopedGroups({ symptoms, issues, scopeIssueId }) {
  const issuesById = {};
  (issues || []).forEach((i) => { issuesById[i.id] = i; });
  let groups = groupEntriesByCondition(symptoms || [], issuesById);
  if (scopeIssueId) groups = groups.filter((g) => g.key === scopeIssueId);
  return groups;
}

function buildInfo({ workspace, profile, master526ez, headerTitle }) {
  return {
    workspaceTitle: workspace?.title || "VA Claim Workspace",
    veteranName: master526ez?.veteranName || profile?.name || "Veteran",
    generatedAt: new Date().toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" }),
    headerTitle,
  };
}

function shortDate(iso) {
  return formatDate(iso).split(",")[0];
}

/** Labeled list block: small brand label + indented body lines (wrapped). */
function block(c, label, lines, { size = 9 } = {}) {
  const rows = (lines || []).filter(Boolean);
  if (!rows.length) return;
  c.ensure(30);
  c.text(label.toUpperCase(), M, c.y, { size: 8, bold: true, color: C_BRAND });
  c.y -= 12;
  rows.forEach((line) => {
    wrap(line, 100).forEach((l) => {
      c.ensure(12);
      c.text(l, M + 6, c.y, { size });
      c.y -= size + 2;
    });
  });
  c.y -= 6;
}

/** Boxed paragraph with a label — used for the appointment-ready summary. */
function summaryBox(c, label, text) {
  const lines = wrap(text, 88);
  const boxH = lines.length * 11 + 30;
  c.ensure(boxH + 10);
  const top = c.y;
  c.rect(M, top - boxH, PAGE_W - M * 2, boxH, { fill: C_BG, stroke: C_BORDER });
  c.text(label.toUpperCase(), M + 12, top - 16, { size: 8, bold: true, color: C_BRAND });
  let y = top - 30;
  lines.forEach((l) => { c.text(l, M + 12, y, { size: 9 }); y -= 11; });
  c.y = top - boxH - 12;
}

function statRow(c, cells) {
  const perRow = 3;
  const gap = 12;
  const cellW = (PAGE_W - M * 2 - gap * (perRow - 1)) / perRow;
  for (let r = 0; r < Math.ceil(cells.length / perRow); r += 1) {
    c.ensure(60);
    const rowCells = cells.slice(r * perRow, r * perRow + perRow);
    const top = c.y;
    rowCells.forEach((cell, i) => {
      const x = M + i * (cellW + gap);
      c.rect(x, top - 48, cellW, 48, { fill: C_BG, stroke: C_BORDER });
      c.text(cell.label, x + 10, top - 16, { size: 7, bold: true, color: C_MUTED });
      c.text(cell.value, x + 10, top - 38, { size: 15, bold: true, color: cell.color || C_BRAND_DARK });
    });
    c.y = top - 56;
  }
  c.y -= 4;
}

function disclaimerPage(c, info) {
  c.newPage("Disclaimer");
  drawHeader(c, info);
  c.y = PAGE_H - 60;
  c.heading("Important Disclaimer", C_RED);
  [
    "This document is a VETERAN-REPORTED symptom diary export generated by ClaimOps VA. It is NOT a medical record, NOT a diagnosis, NOT medical advice, and NOT a guaranteed VA rating tool.",
    CREDIBILITY_NOTE,
    "Entries reflect what the veteran logged on the dates shown. Severity scores (0-10), durations, triggers, and impact descriptions are subjective self-reports.",
    "Examiners, providers, and adjudicators should pair this summary with the official medical record, service treatment record, and any C&P exam findings before making any determination.",
    "All rating criteria and diagnostic codes must be independently verified at VA.gov and eCFR.gov.",
  ].forEach((p) => { c.para(p, { size: 10, width: 92 }); c.y -= 4; });
}

function finishFooters(c, info) {
  const total = c.pages.length;
  c.pages.forEach((p, idx) => {
    c.page = p.page;
    c.section = p.section;
    drawFooter(c, idx + 1, total, info);
  });
}

// ============================================================================
// 1) SYMPTOM EVIDENCE SUMMARY
// ============================================================================

function buildConditionEvidencePages(c, info, group) {
  const s = buildEvidenceSummary({ entries: group.entries, conditionName: group.name });

  c.newPage(group.name);
  drawHeader(c, info);
  drawWatermark(c, c.isFree);
  c.y = PAGE_H - 60;
  c.heading(group.name);
  c.text(`Date range: ${s.dateRangeLabel || "n/a"}  |  ${s.entryCount} entr${s.entryCount === 1 ? "y" : "ies"}  |  ~${s.perWeek}/week`, M, c.y, { size: 9, color: C_MUTED });
  c.y -= 18;

  statRow(c, [
    { label: "SYMPTOM ENTRIES", value: `${s.entryCount}` },
    { label: "FLARE-UPS", value: `${s.flareCount}`, color: s.flareCount ? C_RED : C_BRAND_DARK },
    { label: "AVG SEVERITY", value: s.entryCount ? `${s.avgSeverity}/10` : "-" },
    { label: "WORST SEVERITY", value: s.entryCount ? `${s.worstSeverity}/10` : "-", color: severityColor(s.worstSeverity) },
    { label: "AVG FLARE LENGTH", value: s.avgFlareDurationHours != null ? fmtHours(s.avgFlareDurationHours) : "-" },
    { label: "MISSED DAYS", value: `${s.missedDaysTotal || 0}` },
  ]);

  block(c, "What was logged", s.topSymptoms.slice(0, 6).map((r) => `${r.label} - ${r.value}x`));
  block(c, "Most common triggers", s.topTriggers.slice(0, 6).map((r) => `${r.label} - ${r.value}x`));
  block(
    c, "Medications taken",
    s.medsUsed.slice(0, 6).map((m) => `${m.name} - taken on ${m.taken} entr${m.taken === 1 ? "y" : "ies"}${m.helpedLabel ? ` (most often: ${m.helpedLabel})` : ""}`),
  );
  block(c, "Treatments used", [
    ...s.treatmentsUsed.slice(0, 6).map((r) => `${r.label} - ${r.value}x`),
    ...(s.copingNoted.length ? [`Other coping noted: ${s.copingNoted.join("; ")}`] : []),
  ]);
  block(c, "What provides relief", s.topReliefs.slice(0, 6).map((r) => `${r.label} - ${r.value}x`));
  block(c, "Functional impact (most frequent)", s.topImpacts.slice(0, 7).map((r) => `${r.label} - ${r.value}x`));
  block(c, "Missed work / PT / duty", [
    ...Object.entries(s.missedByActivity).map(([k, v]) => `${k} - missed on ${v} entr${v === 1 ? "y" : "ies"}`),
    ...(s.missedDaysTotal ? [`Approximately ${s.missedDaysTotal} total day${s.missedDaysTotal === 1 ? "" : "s"} missed.`] : []),
  ]);
  block(c, "Assistive devices used", s.assistiveDevices.length ? [s.assistiveDevices.join(", ")] : []);
  block(
    c, "Worst-day examples",
    s.worstDayExamples.map((w) => `${shortDate(w.date)} - ${w.severity}/10 ${w.symptom}${w.notes ? ` - "${w.notes}"` : ""}`),
  );
  block(c, "Repeated-use impact", s.repeatedUseNotes.map((r) => `${shortDate(r.date)} - ${r.text}`));
  block(c, "Veteran notes (most recent)", s.notesExcerpts.map((n) => `${shortDate(n.date)} - ${n.text}`));

  summaryBox(c, "Appointment-ready summary", s.appointmentSummary);
}

function buildEpisodesSection(c, info, episodes = []) {
  if (!episodes || episodes.length === 0) return;
  c.newPage("Ongoing Episodes");
  drawHeader(c, info);
  drawWatermark(c, c.isFree);
  c.y = PAGE_H - 60;
  c.heading("Ongoing Symptom Episodes");
  c.text(
    "Multi-day flares and chronic stretches the veteran tracked as a single episode. Each block shows total days, severity range, and the VA-evidence narrative.",
    M, c.y, { size: 9, color: C_MUTED },
  );
  c.y -= 18;
  episodes.forEach((bundle) => {
    const summary = buildEpisodeSummary(bundle.episode, bundle.days || []);
    const narrative = buildEpisodeNarrative(summary);
    c.ensure(120);
    c.text(
      `${summary.symptom || "Episode"}${summary.bodySide ? ` (${summary.bodySide})` : ""}`,
      M, c.y, { size: 12, bold: true, color: C_BRAND_DARK },
    );
    c.y -= 14;
    c.text(
      `${summary.totalDays} day${summary.totalDays === 1 ? "" : "s"}  |  worst ${summary.worstSeverity}/10  |  avg ${summary.avgSeverity}/10  |  ${summary.dayCount} log${summary.dayCount === 1 ? "" : "s"}${summary.status === "ended" ? "  |  ended" : "  |  ongoing"}`,
      M, c.y, { size: 9, color: C_MUTED },
    );
    c.y -= 14;
    wrap(narrative, 100).forEach((line) => {
      c.ensure(12);
      c.text(line, M + 6, c.y, { size: 10 });
      c.y -= 12;
    });
    c.y -= 6;
    if (summary.medications.length) block(c, "Medications", [summary.medications.join(", ")]);
    if (summary.treatments.length) block(c, "Treatments", [summary.treatments.join(", ")]);
    if (summary.triggers.length) block(c, "Triggers / factors", [summary.triggers.join(", ")]);
    if (summary.impact.length) block(c, "Functional impact", [summary.impact.join(", ")]);
    if (summary.missedDaysTotal) block(c, "Missed work / PT / duty", [`${summary.missedDaysTotal} day${summary.missedDaysTotal === 1 ? "" : "s"} total`]);
    c.y -= 10;
  });
}

export async function createEvidenceSummaryPdf({
  workspace, profile, master526ez, issues = [], symptoms = [], episodes = [],
  scopeIssueId = "", isPro = true,
}) {
  const groups = scopedGroups({ symptoms, issues, scopeIssueId });
  if (!groups.length && (!episodes || !episodes.length)) {
    throw new Error("No symptom entries to report for that scope.");
  }

  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const c = new Canvas(pdfDoc, font, bold);
  c.isFree = !isPro;

  const info = buildInfo({ workspace, profile, master526ez, headerTitle: "CLAIMOPS SYMPTOM EVIDENCE" });

  // Title page
  c.newPage("Symptom Evidence Summary");
  drawHeader(c, info);
  drawWatermark(c, c.isFree);
  c.y = PAGE_H - 80;
  c.text("SYMPTOM EVIDENCE SUMMARY", M, c.y, { size: 22, bold: true, color: C_BRAND_DARK });
  c.y -= 20;
  c.text(`Prepared for ${info.veteranName}  |  ${info.workspaceTitle}`, M, c.y, { size: 11, color: C_MUTED });
  c.y -= 14;
  c.text(`Generated ${info.generatedAt}`, M, c.y, { size: 9, color: C_MUTED });
  c.y -= 28;
  summaryBox(c, "Accuracy & credibility", CREDIBILITY_NOTE);
  c.sectionTitle("What this document is");
  [
    "A per-condition rollup of your symptom diary: frequency, severity, duration, triggers, medications and treatment, functional impact, and missed work / PT / duty.",
    "Built only from the entries you logged - nothing is generated or estimated beyond simple averages and counts.",
    "Hand it to your treating provider, your VSO, or bring it to a C&P exam alongside your medical records.",
  ].forEach((t) => { c.para(t, { size: 10 }); c.y -= 3; });
  c.y -= 6;
  c.sectionTitle("Conditions covered");
  groups.forEach((g) => {
    c.ensure(14);
    c.text(`- ${g.name} (${g.entries.length} entr${g.entries.length === 1 ? "y" : "ies"})`, M + 6, c.y, { size: 10, bold: true });
    c.y -= 14;
  });

  groups.forEach((g) => buildConditionEvidencePages(c, info, g));
  buildEpisodesSection(c, info, episodes);
  disclaimerPage(c, info);
  finishFooters(c, info);
  return await pdfDoc.save();
}

// ============================================================================
// 2) C&P EXAM PREP SHEET (one page per condition)
// ============================================================================

function buildCpPrepPage(c, info, group) {
  const s = buildEvidenceSummary({ entries: group.entries, conditionName: group.name });

  c.newPage(`C&P Prep - ${group.name}`);
  drawHeader(c, info);
  drawWatermark(c, c.isFree);
  c.y = PAGE_H - 60;
  c.heading(`C&P Exam Prep - ${group.name}`);
  c.text(`${s.dateRangeLabel || "No dates"}  |  ${s.entryCount} entries  |  ${s.flareCount} flare-ups`, M, c.y, { size: 9, color: C_MUTED });
  c.y -= 16;

  // Memory-aid framing box
  const note = "MEMORY AID - NOT A SCRIPT. These are facts from your own symptom log. Answer the examiner honestly in your own words; use this sheet so you don't forget frequency, duration, and worst-day details under pressure.";
  const noteLines = wrap(note, 96);
  const noteH = noteLines.length * 10 + 16;
  c.rect(M, c.y - noteH, PAGE_W - M * 2, noteH, { fill: C_BG, stroke: C_GREEN });
  let ny = c.y - 14;
  noteLines.forEach((l) => { c.text(l, M + 10, ny, { size: 8, color: C_MUTED }); ny -= 10; });
  c.y -= noteH + 12;

  const blocks = [
    ["1. What happens", s.topSymptoms.slice(0, 4).map((r) => `${r.label} (${r.value}x)`)],
    ["2. How often", [
      `${s.entryCount} logged entr${s.entryCount === 1 ? "y" : "ies"} over ${s.dateRangeLabel || "the tracking period"}${s.rangeDays > 6 ? ` (~${s.perWeek}/week)` : ""}`,
      `${s.flareCount} flare-up${s.flareCount === 1 ? "" : "s"} logged`,
    ]],
    ["3. How long it lasts", [s.avgFlareDurationHours != null ? `Typical flare: about ${fmtHours(s.avgFlareDurationHours)}` : "Flare durations not logged yet - note start and end time of your next flare."]],
    ["4. What triggers it", s.topTriggers.slice(0, 5).map((r) => `${r.label} (${r.value}x)`)],
    ["5. What helps", [
      ...s.topReliefs.slice(0, 3).map((r) => r.label),
      ...s.medsUsed.slice(0, 3).map((m) => `${m.name}${m.helpedLabel ? ` - ${m.helpedLabel.toLowerCase()}` : ""}`),
    ]],
    ["6. Work & daily life impact", [
      ...s.topImpacts.slice(0, 5).map((r) => `${r.label} (${r.value}x)`),
      ...(Object.keys(s.missedByActivity).length
        ? [`Missed: ${Object.entries(s.missedByActivity).map(([k, v]) => `${k} x${v}`).join(", ")}${s.missedDaysTotal ? ` (~${s.missedDaysTotal} days total)` : ""}`]
        : []),
    ]],
    ["7. Worst-day examples", s.worstDayExamples.map((w) => `${shortDate(w.date)} - ${w.severity}/10 ${w.symptom}${w.notes ? ` - ${w.notes}` : ""}`)],
    ["8. Repeated-use impact", s.repeatedUseNotes.map((r) => r.text)],
    ["9. Treatment history", [
      ...s.treatmentsUsed.slice(0, 5).map((r) => `${r.label} (${r.value}x)`),
      ...(s.assistiveDevices.length ? [`Devices: ${s.assistiveDevices.join(", ")}`] : []),
      ...(s.copingNoted.length ? [`Coping: ${s.copingNoted.join("; ")}`] : []),
    ]],
  ];

  blocks.forEach(([label, lines]) => block(c, label, lines, { size: 8.5 }));
}

export async function createCpPrepPdf({
  workspace, profile, master526ez, issues = [], symptoms = [], scopeIssueId = "", isPro = true,
}) {
  const groups = scopedGroups({ symptoms, issues, scopeIssueId });
  if (!groups.length) throw new Error("No symptom entries to report for that scope.");

  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const c = new Canvas(pdfDoc, font, bold);
  c.isFree = !isPro;

  const info = buildInfo({ workspace, profile, master526ez, headerTitle: "CLAIMOPS C&P EXAM PREP" });

  groups.forEach((g) => buildCpPrepPage(c, info, g));
  disclaimerPage(c, info);
  finishFooters(c, info);
  return await pdfDoc.save();
}
